/**
 * `/admin/sync` — TASK 10.4 Step 4 (US-3: AC 3.5a; mock-defect fix 12).
 *
 * The mock's `_adminSync(V,q)` view model (L1134-1141) and template
 * (L679-698), wired to the real status API (TASK 3.5) instead of `?state=`:
 *
 *   - the six-field readout via `sync-stat-<label>` tiles — all values come
 *     from the durable `service_counter` store (+ catalog count), zero-seeded
 *     in migration 0001, so they are NEVER null once a run exists;
 *   - **mock-defect fix 12 — error vs degraded are DISTINGUISHABLE:** the
 *     mock collapsed both into one pink banner; here the `sync-state-chip`
 *     carries five distinct states (HEALTHY / RUNNING / DEGRADED / ERROR /
 *     NEVER-RUN), each with its own text label + icon + `data-state` (border
 *     + icon + text, never colour alone — §8.5), and DEGRADED and ERROR get
 *     DIFFERENT banners with different copy;
 *   - `never_run` renders the FIRST-RUN panel (mock L688) instead of a stats
 *     grid of nulls;
 *   - "Sync now" while a run is in flight answers an INLINE `role="status"`
 *     notice — the button is NOT disabled and no toast fires (the server's
 *     `already_running` is a 200, not an error);
 *   - loading skeleton + retriable error for the status read.
 *
 * Member access (AC 2.7) is handled by the shell (MoreMenu + router bounce)
 * and enforced by `requireAdmin` server-side; this view assumes an admin.
 *
 * @module
 */

/* global AbortController -- browser platform API; eslint.config.js's browser
   globals block predates the views using it and is outside TASK 10.4's files */

import { TESTIDS, syncStatCard } from '@streamymusic/shared';

import { ErrorBlock, LoadingBlock } from '../../components/StateBlocks.js';
import { Pill } from '../../components/primitives/Pill.js';
import { append, el } from '../../components/primitives/dom.js';

import { AdminNav } from './AdminNav.js';

/**
 * Above this error-rate the catalog/stream readouts count as DEGRADED (the
 * mock's degraded fixture ran at 2.4% streaming / 1.1% catalog; the alerting
 * SLI (TASK 11.1) draws its own line at 1% — mirrored here so the admin
 * screen and the pager agree about "elevated").
 */
export const DEGRADED_ERROR_RATE_THRESHOLD = 0.01;

/** mock L686 — the inline `role=status` notice, verbatim. */
export const SYNC_ALREADY_RUNNING_MESSAGE =
  'A sync is already running — no second run was started.';
/** Net-new (the mock faked completion with a timer): trigger acknowledged. */
export const SYNC_STARTED_MESSAGE = 'Sync started — the catalog is reindexing.';
export const SYNC_TRIGGER_FAILED_MESSAGE = 'Couldn’t start a sync. Give it another shot.';
/** mock L1141 — the inline notice auto-clears. */
export const SYNC_MESSAGE_TTL_MS = 3500;

/** mock L688 — the first-run panel, verbatim. */
export const SYNC_NEVER_RUN_HEADLINE = 'NO SYNC HAS RUN YET';
export const SYNC_NEVER_RUN_MESSAGE =
  'Kick off the first index with "Sync now" — once it finishes, stats show up here.';

/** Distinct DEGRADED vs ERROR banners (mock-defect fix 12; mock L691 had one). */
export const SYNC_DEGRADED_BANNER =
  '⚠ DEGRADED — the catalog is still serving, but telemetry writes are failing and/or the error rate is elevated. Investigate before the next sync.';
export const SYNC_ERROR_BANNER =
  '✖ ERROR — the last sync run failed. The catalog keeps serving the previous index; check the run logs, then try "Sync now".';

/**
 * @typedef {'healthy'|'running'|'degraded'|'error'|'never-run'} SyncState
 */

/**
 * mock L1134-1135 collapsed to a pure decision over the REAL status payload.
 * Priority order: a run in flight beats everything (the readout says so);
 * `never_run` beats health math (there is nothing to be healthy about);
 * a FAILED last run is ERROR; elevated error rates / telemetry failures on a
 * succeeded run are DEGRADED; otherwise HEALTHY.
 *
 * @param {import('@streamymusic/shared').SyncStatusResponse} status
 * @param {number} [threshold]
 * @returns {SyncState}
 */
export function syncStateOf(status, threshold = DEGRADED_ERROR_RATE_THRESHOLD) {
  if (status.running || status.lastRunStatus === 'running') return 'running';
  if (status.lastRunStatus === 'never_run') return 'never-run';
  if (status.lastRunStatus === 'failed') return 'error';
  if (
    status.telemetryWriteFailures > 0 ||
    status.streamingErrorRate > threshold ||
    status.catalogErrorRate > threshold
  ) {
    return 'degraded';
  }
  return 'healthy';
}

/**
 * mock L1135 (`label`/`bg`) + fix 12: every state carries its OWN icon and
 * label so no two states are distinguishable by colour alone.
 *
 * @type {Record<SyncState, { label: string, icon: string, background: string }>}
 */
export const SYNC_STATE_CHIP = {
  healthy: { label: 'HEALTHY', icon: '✓', background: 'var(--sm-color-teal)' },
  running: { label: 'RUNNING', icon: '↻', background: 'var(--sm-color-teal)' },
  degraded: { label: 'DEGRADED', icon: '⚠', background: 'var(--sm-color-yellow)' },
  error: { label: 'ERROR', icon: '✖', background: 'var(--sm-color-pink)' },
  'never-run': { label: 'NEVER RUN', icon: '◌', background: 'var(--sm-color-text-dim-2)' },
};

/**
 * `0.024` -> `"2.4%"` (mock L1140's seeded strings, computed for real).
 *
 * @param {number} rate
 * @returns {string}
 */
export function formatRate(rate) {
  return `${(rate * 100).toFixed(1)}%`;
}

/**
 * @param {string|null} iso
 * @returns {string}
 */
function formatRunTime(iso) {
  if (iso === null) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * The admin-sync API surface this view drives (TASK 3.5 routes,
 * `apps/api/src/routes/admin-sync.ts`); the shared ApiClient grows these
 * methods in the composition root's wiring step.
 *
 * @typedef {object} AdminSyncApi
 * @property {(options?: { signal?: AbortSignal }) =>
 *   Promise<import('@streamymusic/shared').SyncStatusResponse>} getSyncStatus
 *   `GET /api/admin/sync`
 * @property {() => Promise<import('@streamymusic/shared').SyncTriggerResponse>} triggerSync
 *   `POST /api/admin/sync` — `already_running` is a 200 outcome, not an error
 */

/**
 * Builds the `/admin/sync` section and starts loading the status readout.
 *
 * @param {{
 *   api: AdminSyncApi,
 *   heading: HTMLElement,
 *   toast: (text: string, tone?: 'ok'|'err'|'info') => void,
 *   announce?: (text: string) => void,
 * }} options
 *   `heading` is the route's `<h1 data-testid="page-heading">` — handed in by
 *   the shell, never created here.
 * @returns {{ element: HTMLElement, dispose: () => void }}
 */
export function SyncView(options) {
  const { api, heading, toast, announce } = options;

  heading.textContent = 'SYNC ENGINE';

  const section = el('section', { attrs: { 'data-screen-label': 'Admin sync' } });
  append(section, heading, AdminNav({ active: 'sync' }));

  /* ── header row: state chip + Sync now (mock L682-684) ────────────────── */

  const chipHost = el('span');
  const syncNowButton = el('button', {
    className: 'sm-btn sm-btn--primary sm-btn--display-17',
    text: '↻ SYNC NOW',
    testId: TESTIDS.SYNC_NOW_BUTTON,
    attrs: { type: 'button' },
    on: {
      click: () => {
        void triggerSync();
      },
    },
  });
  const headerRow = append(
    el('div', { className: 'sm-flex-row-12-wrap' }),
    chipHost,
    syncNowButton,
  );
  section.append(headerRow);

  /*
   * mock L686 — the inline notice. The region EXISTS before its text changes
   * (a node inserted already containing text is not reliably announced — see
   * StateBlocks.js's header), so `role=status` is honoured.
   */
  const message = el('div', {
    className: 'sm-banner sm-banner--sync',
    attrs: { role: 'status', hidden: true },
  });
  const body = el('div');
  append(section, message, body);

  /* ── state ────────────────────────────────────────────────────────────── */

  /** @type {import('@streamymusic/shared').SyncStatusResponse|null} */
  let status = null;
  let generation = 0;
  let disposed = false;
  /** @type {AbortController|null} */
  let controller = null;
  let triggering = false;
  /** @type {ReturnType<typeof setTimeout>|null} */
  let messageTimer = null;

  /** @param {string} text */
  function showMessage(text) {
    message.textContent = text;
    message.removeAttribute('hidden');
    if (announce !== undefined) announce(text);
    if (messageTimer !== null) clearTimeout(messageTimer);
    messageTimer = setTimeout(() => {
      messageTimer = null;
      message.textContent = '';
      message.setAttribute('hidden', '');
    }, SYNC_MESSAGE_TTL_MS);
  }

  /* ── painting ─────────────────────────────────────────────────────────── */

  /**
   * @param {string} label
   * @param {string} value
   * @param {string} [color]
   * @returns {HTMLElement}
   */
  function statTile(label, value, color) {
    const tile = el('div', {
      className: 'sm-panel sm-panel--tile',
      testId: syncStatCard(label),
    });
    append(
      tile,
      el('div', { className: 'sm-tile__label', text: label }),
      el('div', {
        className: 'sm-tile__value',
        text: value,
        // mock `{{ s.col }}` — dynamic per state, so CSSOM.
        ...(color === undefined ? {} : { styleProps: { color } }),
      }),
    );
    return tile;
  }

  function paint() {
    if (status === null) return;
    const state = syncStateOf(status);
    const chipSpec = SYNC_STATE_CHIP[state];

    // mock L683 — the state chip; `data-state` is the semantic hook that
    // makes error-vs-degraded machine-distinguishable (fix 12).
    const chip = Pill({
      label: chipSpec.label,
      icon: chipSpec.icon,
      tone: 'sync',
      background: chipSpec.background,
      testId: TESTIDS.SYNC_STATE_CHIP,
    });
    chip.dataset.state = state;
    chipHost.replaceChildren(chip);

    if (state === 'never-run') {
      // mock L688 — the first-run panel; NO stat tiles, so no null figures.
      const panel = el('div', { className: 'sm-empty sm-empty--16' });
      append(
        panel,
        el('div', { className: 'sm-glyph sm-glyph--empty', text: SYNC_NEVER_RUN_HEADLINE }),
        el('p', { className: 'sm-body sm-body--empty', text: SYNC_NEVER_RUN_MESSAGE }),
      );
      body.replaceChildren(panel);
      return;
    }

    const content = el('div');

    // Fix 12 — DISTINCT banners: degraded is a yellow ⚠ advisory, error a
    // pink ✖ failure. Border + icon + copy differ, not just colour.
    if (state === 'degraded') {
      content.append(
        el('div', {
          className: 'sm-banner sm-banner--mb',
          text: SYNC_DEGRADED_BANNER,
          attrs: { role: 'status' },
        }),
      );
    } else if (state === 'error') {
      content.append(
        el('div', {
          className: 'sm-banner sm-banner--danger',
          text: SYNC_ERROR_BANNER,
          attrs: { role: 'status' },
        }),
      );
    }

    const running = state === 'running';
    /** @param {number} rate */
    const elevated = (rate) => rate > DEGRADED_ERROR_RATE_THRESHOLD;
    const grid = el('div', { className: 'sm-grid-tiles' });
    append(
      grid,
      // mock L1140 — the six tiles, labels verbatim (they derive the
      // `sync-stat-<label>` testids via the shared factory).
      statTile('Last run', running ? '…running' : formatRunTime(status.lastRunAt)),
      statTile(
        'Outcome',
        running ? 'Running' : status.lastRunStatus === 'failed' ? 'Failed' : 'Success',
        status.lastRunStatus === 'failed' ? 'var(--sm-color-pink)' : 'var(--sm-color-teal)',
      ),
      statTile('Tracks indexed', status.tracksIndexed.toLocaleString()),
      statTile(
        'Streaming err rate',
        formatRate(status.streamingErrorRate),
        elevated(status.streamingErrorRate) ? 'var(--sm-color-yellow)' : 'var(--sm-color-teal)',
      ),
      statTile(
        'Catalog err rate',
        formatRate(status.catalogErrorRate),
        elevated(status.catalogErrorRate) ? 'var(--sm-color-yellow)' : 'var(--sm-color-teal)',
      ),
      statTile(
        'Telemetry write fails',
        String(status.telemetryWriteFailures),
        status.telemetryWriteFailures > 0 ? 'var(--sm-color-pink)' : 'var(--sm-color-teal)',
      ),
    );
    content.append(grid);
    body.replaceChildren(content);
  }

  /* ── loading ──────────────────────────────────────────────────────────── */

  /**
   * @param {{ quiet?: boolean }} [opts]
   * @returns {Promise<void>}
   */
  async function load(opts = {}) {
    const quiet = opts.quiet === true;
    const own = (generation += 1);
    controller?.abort();
    const ownController = new AbortController();
    controller = ownController;

    if (!quiet) body.replaceChildren(LoadingBlock({ count: 4, label: 'Loading sync status' }));

    try {
      const fresh = await api.getSyncStatus({ signal: ownController.signal });
      if (disposed || own !== generation) return;
      status = fresh;
      paint();
    } catch {
      if (disposed || own !== generation) return;
      if (quiet) return; // the previous readout is still standing
      body.replaceChildren(
        ErrorBlock({
          onRetry: () => {
            void load();
          },
          ...(announce === undefined ? {} : { announce }),
        }),
      );
    }
  }

  /* ── manual trigger ───────────────────────────────────────────────────── */

  /**
   * `already_running` is a 200 OUTCOME: inline `role=status` notice, button
   * stays enabled, NO toast (the spec's exact shape). Only a transport/server
   * failure toasts.
   *
   * @returns {Promise<void>}
   */
  async function triggerSync() {
    if (triggering || disposed) return;
    triggering = true;
    try {
      const result = await api.triggerSync();
      if (disposed) return;
      if (result.status === 'already_running') {
        showMessage(SYNC_ALREADY_RUNNING_MESSAGE);
        return;
      }
      showMessage(SYNC_STARTED_MESSAGE);
      void load({ quiet: true });
    } catch {
      if (disposed) return;
      toast(SYNC_TRIGGER_FAILED_MESSAGE, 'err');
    } finally {
      triggering = false;
    }
  }

  void load();

  return {
    element: section,
    dispose() {
      disposed = true;
      controller?.abort();
      if (messageTimer !== null) clearTimeout(messageTimer);
    },
  };
}
